import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, CheckCircle, Loader, Clock, Brain, Shield, Play, ChevronRight, Zap, Send, GitBranch, Paperclip, Mic, RefreshCw } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { XR1_EXECUTION, SHARE_DETAILS_PROMPTS, REQUEST_CHANGE_RESPONSES } from '../../data/executionData';

const AGENT_NAMES = { stark: 'Stark', luc: 'Luc', marsi: 'Marsi', sally: 'Sally', zira: 'Zira', tali: 'Tali', finn: 'Finn', wright: 'Wright', buzz: 'Buzz', dora: 'Dora' };
const AGENT_COLORS = {
  zira: { dark: 'text-blue-400', light: 'text-blue-600' },
  finn: { dark: 'text-amber-400', light: 'text-amber-700' },
  sally: { dark: 'text-emerald-400', light: 'text-emerald-600' },
  marsi: { dark: 'text-purple-400', light: 'text-purple-600' },
  wright: { dark: 'text-rose-400', light: 'text-rose-600' },
  stark: { dark: 'text-cyan-400', light: 'text-cyan-600' },
  luc: { dark: 'text-orange-400', light: 'text-orange-600' },
  tali: { dark: 'text-pink-400', light: 'text-pink-600' },
  buzz: { dark: 'text-lime-400', light: 'text-lime-600' },
  dora: { dark: 'text-violet-400', light: 'text-violet-600' },
};
const STEP_INTERVAL = 1800;
const TYPING_SPEED = 15;

const REQUEST_CHANGE_MESSAGES = {
  'outreach-approval': "Zira, tone down the urgency in the TechCorp email — they have a sensitive CTO who doesn't respond well to pressure. Keep DataFlow and Acme Corp emails as-is, those are good.",
  'known-issues-approval': "Zira, add an estimated resolution timeline per issue cluster on the known-issues page. Customers need to know when to expect fixes, not just workarounds.",
  'forecast-approval': "Finn, add a fourth scenario that assumes we recover TechCorp but lose DataFlow — I think that's the most likely outcome based on the account signals Sally flagged.",
  'reengage-approval': "Sally, for the TechCorp email, reference our Q2 product roadmap as a retention hook — they were excited about the API v3 preview. Have Wright incorporate that angle.",
  'comms-approval': "Marsi, the enterprise version feels too corporate. Make it more empathetic — acknowledge the disruption directly. Have Wright soften the language while keeping the factual content.",
};

const StepIcon = ({ type, isDark }) => {
  if (type === 'thinking') return <Brain className={`w-3 h-3 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />;
  if (type === 'result') return <CheckCircle className={`w-3 h-3 flex-shrink-0 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />;
  if (type === 'hitl') return <Shield className={`w-3 h-3 flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />;
  if (type === 'waiting') return <Clock className={`w-3 h-3 flex-shrink-0 ${isDark ? 'text-white/30' : 'text-slate-500'}`} />;
  if (type === 'support-agent') return <Zap className={`w-3 h-3 flex-shrink-0 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />;
  return <Loader className={`w-3 h-3 flex-shrink-0 animate-spin ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />;
};

const ExecutionStep = ({ step, isDark, isNew, onApprove, onRequestChanges, onShareDetails, approved, shareDetailsMap }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const isHitl = step.type === 'hitl';
  const isWaiting = step.type === 'waiting';
  const isSupportAgent = step.type === 'support-agent';
  const isResult = step.type === 'result';

  const bgClass = isHitl && !approved
    ? (isDark ? 'bg-amber-500/8 border-amber-500/15' : 'bg-amber-50 border-amber-200/60')
    : isResult
    ? (isDark ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-emerald-50/60 border-emerald-200/40')
    : isSupportAgent
    ? (isDark ? 'bg-purple-500/5 border-purple-500/10' : 'bg-purple-50/60 border-purple-200/40')
    : `border-transparent`;

  return (
    <div
      className={`flex items-start gap-2 px-2.5 py-2 rounded-lg border ${bgClass} transition-all duration-500 ${isNew ? 'animate-fadeSlideIn' : ''} ${isWaiting ? 'opacity-50' : ''}`}
      data-testid={`exec-step-${step.id}`}
    >
      <div className="mt-0.5">
        <StepIcon type={approved ? 'result' : step.type} isDark={isDark} />
      </div>
      <div className="flex-1 min-w-0">
        {isSupportAgent && (
          <div className="flex items-center gap-1.5 mb-1">
            <img src={AGENT_THUMBS[step.supportAgent]} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-purple-400/10 text-purple-400' : 'bg-purple-600/10 text-purple-600'}`}>
              {AGENT_NAMES[step.supportAgent]} — Copywriting Agent
            </span>
          </div>
        )}
        <p className={`text-[11px] leading-relaxed whitespace-pre-line ${isWaiting ? (isDark ? 'text-white/30' : 'text-slate-500') : textPrimary}`}>
          {step.text}
        </p>
        {isHitl && !approved && (
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => onApprove(step.approvalId)}
              className="flex items-center gap-1 px-3 py-1 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-[10px] font-medium"
              data-testid={`exec-approve-${step.approvalId}`}
            >
              <CheckCircle className="w-3 h-3" /> Approve
            </button>
            <button
              onClick={() => onRequestChanges?.(step.approvalId)}
              className={`flex items-center gap-1 px-3 py-1 rounded-lg text-[10px] font-medium border ${border} ${textSecondary}`}
              data-testid={`exec-request-changes-${step.approvalId}`}
            >
              Request Changes
            </button>
          </div>
        )}
        {isHitl && approved && (
          <div className="flex items-center gap-1.5 mt-1">
            <CheckCircle className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <span className={`text-[10px] font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Approved</span>
          </div>
        )}
        {isResult && shareDetailsMap[step.id] && (
          <button
            onClick={() => onShareDetails?.(step.id)}
            className={`flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-md text-[9px] font-medium transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/15'}`}
            data-testid={`exec-share-details-${step.id}`}
          >
            <ChevronRight className="w-2.5 h-2.5" /> Share Details
          </button>
        )}
      </div>
    </div>
  );
};

/* ─── Per-column chat message (name prefix, no thumbnail) ─── */

const ColumnChatMessage = ({ msg, isDark, approvedSet, onApproveHitl, onRequestChangesHitl }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  if (msg.role === 'user') {
    return (
      <div className="flex justify-end px-2.5 py-1" data-testid="col-chat-user-msg">
        <div className={`max-w-[92%] px-2.5 py-1.5 rounded-xl rounded-br-sm text-[11px] leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}`}>
          {msg.text}
        </div>
      </div>
    );
  }

  if (msg.role === 'system') {
    return (
      <div className="flex justify-center px-2 py-1" data-testid="col-chat-system-msg">
        <span className={`text-[9px] font-medium px-2 py-0.5 rounded-full ${isDark ? 'bg-emerald-400/10 text-emerald-400' : 'bg-emerald-600/10 text-emerald-600'}`}>
          <CheckCircle className="w-2.5 h-2.5 inline mr-0.5" />{msg.text}
        </span>
      </div>
    );
  }

  if (msg.role === 'cascade-notify') {
    return (
      <div className="px-2.5 py-1.5 animate-fadeSlideIn" data-testid="col-chat-cascade-notify">
        <div className={`flex items-start gap-2 px-2.5 py-2 rounded-lg border ${isDark ? 'bg-indigo-500/5 border-indigo-500/10' : 'bg-indigo-50/60 border-indigo-200/40'}`}>
          <GitBranch className={`w-3 h-3 flex-shrink-0 mt-0.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
          <p className={`text-[10px] leading-relaxed ${isDark ? 'text-indigo-300/80' : 'text-indigo-700/80'}`}>{msg.text}</p>
        </div>
      </div>
    );
  }

  if (msg.role === 'revised-hitl') {
    const isApproved = approvedSet?.has(msg.approvalId);
    return (
      <div className="px-2.5 py-1.5 animate-fadeSlideIn" data-testid="col-chat-revised-hitl">
        <div className={`px-2.5 py-2 rounded-lg border ${isApproved ? (isDark ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-emerald-50/60 border-emerald-200/40') : (isDark ? 'bg-amber-500/8 border-amber-500/15' : 'bg-amber-50 border-amber-200/60')}`}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Shield className={`w-3 h-3 ${isApproved ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-amber-400' : 'text-amber-600')}`} />
            <span className={`text-[9px] font-semibold uppercase tracking-wider ${isApproved ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-amber-400' : 'text-amber-600')}`}>
              {isApproved ? 'Revision Approved' : 'Revised — Needs Approval'}
            </span>
          </div>
          <p className={`text-[11px] leading-relaxed whitespace-pre-line ${textPrimary}`}>{msg.text}</p>
          {!isApproved && (
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={() => onApproveHitl?.(msg.approvalId)}
                className="flex items-center gap-1 px-3 py-1 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-[10px] font-medium"
                data-testid={`revised-approve-${msg.approvalId}`}
              >
                <CheckCircle className="w-3 h-3" /> Approve
              </button>
              <button
                onClick={() => onRequestChangesHitl?.(msg.approvalId)}
                className={`flex items-center gap-1 px-3 py-1 rounded-lg text-[10px] font-medium border ${border} ${textSecondary}`}
                data-testid={`revised-request-changes-${msg.approvalId}`}
              >
                Request Changes
              </button>
            </div>
          )}
          {isApproved && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <CheckCircle className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
              <span className={`text-[10px] font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Approved</span>
            </div>
          )}
        </div>
        {/* Cascade preview — shown after revised HITL, before approval */}
        {!isApproved && msg.hasCascade && msg.cascadeEffects?.length > 0 && (
          <div className={`mt-2 px-2.5 py-2 rounded-lg border ${isDark ? 'bg-indigo-500/5 border-indigo-500/10' : 'bg-indigo-50/60 border-indigo-200/30'}`}>
            <div className="flex items-center gap-1.5 mb-2">
              <GitBranch className={`w-3 h-3 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <span className={`text-[9px] font-semibold uppercase tracking-wider ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>This change will impact</span>
            </div>
            <div className="space-y-1.5">
              {msg.cascadeEffects.map((effect, i) => (
                <div key={i} className="flex items-start gap-2">
                  <img src={AGENT_THUMBS[effect.agent]} alt="" className="w-4 h-4 rounded-full object-cover flex-shrink-0 mt-0.5" />
                  <div>
                    <span className={`text-[10px] font-semibold ${textPrimary}`}>{AGENT_NAMES[effect.agent]}</span>
                    <span className={`text-[10px] ${textSecondary}`}> — {effect.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const agentColor = AGENT_COLORS[msg.agent]?.[isDark ? 'dark' : 'light'] || textPrimary;

  return (
    <div className="px-2.5 py-1.5" data-testid="col-chat-agent-msg">
      <div className="space-y-1">
        <div className={`text-[11px] leading-relaxed whitespace-pre-line ${textPrimary}`}>
          <span className={`font-semibold ${agentColor}`}>{AGENT_NAMES[msg.agent]}: </span>
          {msg.text}
        </div>
        {msg.followUp && (
          <div className={`text-[10px] leading-relaxed italic ${isDark ? 'text-indigo-300/80' : 'text-indigo-600/80'}`}>{msg.followUp}</div>
        )}
        {msg.hitlShortcut && !approvedSet?.has(msg.hitlShortcut.approvalId) && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <button
              onClick={() => onApproveHitl?.(msg.hitlShortcut.approvalId)}
              className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-[9px] font-medium"
              data-testid={`col-hitl-approve-${msg.hitlShortcut.approvalId}`}
            >
              <CheckCircle className="w-2.5 h-2.5" /> Approve
            </button>
            <button
              onClick={() => onRequestChangesHitl?.(msg.hitlShortcut.approvalId)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded-lg text-[9px] font-medium border ${border} ${textSecondary}`}
              data-testid={`col-hitl-request-${msg.hitlShortcut.approvalId}`}
            >
              Request Changes
            </button>
          </div>
        )}
        {msg.hitlShortcut && approvedSet?.has(msg.hitlShortcut.approvalId) && (
          <div className="flex items-center gap-1 mt-1">
            <CheckCircle className={`w-2.5 h-2.5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <span className={`text-[9px] font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Approved</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Cascade HITL confirmation ─── */

const CascadeHITLCard = ({ effects, isDark, onApprove, onDecline }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  return (
    <div className={`mx-2.5 my-1.5 px-2.5 py-2 rounded-lg border ${isDark ? 'bg-amber-500/8 border-amber-500/15' : 'bg-amber-50 border-amber-200/60'} animate-fadeSlideIn`} data-testid="cascade-hitl-card">
      <div className="flex items-center gap-1.5 mb-1.5">
        <GitBranch className={`w-3 h-3 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
        <span className={`text-[10px] font-semibold ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>Cascade Confirmation</span>
      </div>
      <p className={`text-[10px] leading-relaxed mb-1.5 ${textPrimary}`}>This change will impact:</p>
      <div className="space-y-1 mb-2">
        {effects.map((e, i) => (
          <div key={i} className="flex items-start gap-1.5">
            <img src={AGENT_THUMBS[e.agent]} alt="" className="w-3.5 h-3.5 rounded-full object-cover flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <span className={`text-[10px] font-medium ${textPrimary}`}>{AGENT_NAMES[e.agent]}</span>
              <span className={`text-[9px] ml-1 ${textSecondary}`}>— {e.description}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1.5">
        <button
          onClick={onApprove}
          className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-[9px] font-medium"
          data-testid="cascade-approve-btn"
        >
          <CheckCircle className="w-2.5 h-2.5" /> Proceed
        </button>
        <button
          onClick={onDecline}
          className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-medium border ${border} ${textSecondary}`}
          data-testid="cascade-decline-btn"
        >
          Keep Local
        </button>
      </div>
    </div>
  );
};

/* ─── Agent column (steps + inline messages, no input) ─── */

const AgentColumn = ({
  column, isDark, visibleStepCount, approvedSet,
  onApprove, onRequestChanges, onShareDetails,
  isBlocked, cascadeSteps,
  chatMessages, isActiveChat,
  pendingCascade, onApproveCascade, onDeclineCascade,
  onApproveHitl, onRequestChangesHitl,
  shareDetailsMap,
}) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const colRef = useRef(null);
  const agentThumb = AGENT_THUMBS[column.agent];

  useEffect(() => {
    if (colRef.current) colRef.current.scrollTop = colRef.current.scrollHeight;
  }, [visibleStepCount, chatMessages?.length, pendingCascade]);

  const visibleSteps = column.steps.slice(0, visibleStepCount);
  const isWaitingPhase = visibleStepCount === 1 && column.steps[0]?.type === 'waiting';
  const isComplete = visibleStepCount >= column.steps.length && !column.steps.some(s => s.type === 'hitl' && !approvedSet.has(s.approvalId));
  const completedCount = visibleSteps.filter(s => s.type === 'result' || (s.type === 'hitl' && approvedSet.has(s.approvalId))).length;
  const totalMilestones = column.steps.filter(s => s.type === 'result' || s.type === 'hitl').length;
  const hasChatActivity = chatMessages && chatMessages.length > 0;

  return (
    <div
      className={`flex flex-col min-h-0 rounded-xl border ${isActiveChat ? (isDark ? 'border-indigo-500/30' : 'border-indigo-400/40') : border} transition-colors overflow-hidden`}
      data-testid={`exec-col-${column.agent}`}
    >
      {/* Header */}
      <div className={`flex items-center gap-2 px-3 py-2.5 flex-shrink-0 border-b ${border} ${isDark ? 'bg-white/[0.02]' : 'bg-black/[0.01]'}`}>
        {agentThumb && <img src={agentThumb} alt="" className="w-6 h-6 rounded-full object-cover" />}
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold ${textPrimary}`}>{AGENT_NAMES[column.agent]}</p>
          <p className={`text-[9px] ${textSecondary}`}>{column.title}</p>
        </div>
        <div className="flex items-center gap-1.5">
          {isComplete ? (
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-emerald-400/10 text-emerald-400' : 'bg-emerald-600/10 text-emerald-600'}`}>Complete</span>
          ) : isWaitingPhase ? (
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-white/[0.06] text-white/40' : 'bg-black/[0.04] text-slate-500'}`}>Waiting</span>
          ) : isBlocked ? (
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full animate-pulse ${isDark ? 'bg-amber-400/10 text-amber-400' : 'bg-amber-600/10 text-amber-600'}`}>Needs Approval</span>
          ) : (
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-blue-400/10 text-blue-400' : 'bg-blue-600/10 text-blue-600'}`}>
              <Loader className="w-2.5 h-2.5 animate-spin inline mr-0.5" /> Executing
            </span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className={`h-0.5 flex-shrink-0 ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.04]'}`}>
        <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-700" style={{ width: `${totalMilestones > 0 ? (completedCount / totalMilestones) * 100 : 0}%` }} />
      </div>

      {/* Steps + Chat (scrollable) */}
      <div ref={colRef} className="flex-1 overflow-y-auto px-2 py-2">
        {visibleSteps.map((step, i) => {
          const timeLabels = ['12s', '23s', '8s', '31s', '15s', '19s', '42s', '7s', '28s', '11s', '35s', '16s', '22s'];
          const timeLabel = timeLabels[(i + column.agent.charCodeAt(0)) % timeLabels.length];
          return (
            <div key={step.id}>
              <ExecutionStep step={step} isDark={isDark} isNew={i === visibleStepCount - 1} onApprove={onApprove} onRequestChanges={onRequestChanges} onShareDetails={onShareDetails} approved={approvedSet.has(step.approvalId)} shareDetailsMap={shareDetailsMap} />
              {i < visibleSteps.length - 1 && (
                <div className="flex items-center gap-1.5 pl-[18px] py-0.5">
                  <div className={`w-px h-4 ${isDark ? 'bg-white/[0.08]' : 'bg-slate-300'}`} />
                  <span className={`text-[8px] font-mono ${isDark ? 'text-white/20' : 'text-slate-500'}`}>{timeLabel}</span>
                </div>
              )}
            </div>
          );
        })}

        {/* Cascade steps */}
        {cascadeSteps && cascadeSteps.length > 0 && (
          <>
            <div className="flex items-center gap-1.5 pl-[18px] py-1 mt-1">
              <div className={`w-px h-3 ${isDark ? 'bg-indigo-400/30' : 'bg-indigo-400'}`} />
              <span className={`text-[8px] font-mono ${isDark ? 'text-indigo-400/40' : 'text-indigo-500'}`}>cascade</span>
            </div>
            {cascadeSteps.map((step) => (
              <div key={step.id} className="animate-fadeSlideIn">
                <ExecutionStep step={step} isDark={isDark} isNew={true} onApprove={onApprove} onRequestChanges={onRequestChanges} onShareDetails={onShareDetails} approved={false} shareDetailsMap={shareDetailsMap} />
              </div>
            ))}
          </>
        )}

        {/* Per-column inline chat messages */}
        {hasChatActivity && (
          <>
            <div className={`border-t ${isDark ? 'border-white/[0.06]' : 'border-slate-200'} my-2 mx-1`} />
            {chatMessages.map((msg, i) => (
              <ColumnChatMessage key={i} msg={msg} isDark={isDark} approvedSet={approvedSet} onApproveHitl={onApproveHitl} onRequestChangesHitl={onRequestChangesHitl} />
            ))}
          </>
        )}

        {/* Cascade HITL gate */}
        {pendingCascade && (
          <CascadeHITLCard effects={pendingCascade.effects} isDark={isDark} onApprove={onApproveCascade} onDecline={onDeclineCascade} />
        )}
      </div>
    </div>
  );
};

/* ─── Main execution view ─── */

const V4ExecutionView = ({ isDark, onBack, onProgressUpdate, executionData, shareDetailsData, requestChangeData, backLabel, requestChangeMessagesData }) => {
  const execution = executionData || XR1_EXECUTION;
  const shareDetails = shareDetailsData || SHARE_DETAILS_PROMPTS;
  const requestChanges = requestChangeData || REQUEST_CHANGE_RESPONSES;
  const backText = backLabel || 'Back to Org Pulse';
  const changeMessages = requestChangeMessagesData || REQUEST_CHANGE_MESSAGES;
  const [visibleCounts, setVisibleCounts] = useState(() => execution.columns.map(() => 0));
  const [approvedSet, setApprovedSet] = useState(new Set());
  const [milestones, setMilestones] = useState(new Set());
  const [started, setStarted] = useState(false);
  const [cascadeStepsMap, setCascadeStepsMap] = useState({});
  const timerRef = useRef(null);

  // Chat state
  const [columnChats, setColumnChats] = useState({});
  const [activeAgent, setActiveAgent] = useState(execution.columns[0]?.agent || 'zira');
  const [chatInput, setChatInput] = useState('');
  const [typingTarget, setTypingTarget] = useState(null);
  const [typingIndex, setTypingIndex] = useState(0);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingCascade, setPendingCascade] = useState(null);
  const inputRef = useRef(null);
  const handleSendRef = useRef(null);

  // Reset all state when execution data changes
  useEffect(() => {
    setVisibleCounts(execution.columns.map(() => 0));
    setApprovedSet(new Set());
    setMilestones(new Set());
    setStarted(false);
    setCascadeStepsMap({});
    setColumnChats({});
    setActiveAgent(execution.columns[0]?.agent || 'zira');
    setChatInput('');
    setTypingTarget(null);
    setTypingIndex(0);
    setPendingAction(null);
    setPendingCascade(null);
    if (timerRef.current) clearInterval(timerRef.current);
  }, [execution.id]);

  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const solidBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';

  const getColumnBlocked = useCallback((colIdx) => {
    const col = execution.columns[colIdx];
    const count = visibleCounts[colIdx];
    if (count <= 0) return false;
    const lastStep = col.steps[count - 1];
    if (lastStep?.type !== 'hitl') return false;
    // Unblocked if approved OR if changes were requested (revised HITL shown in chat)
    return !approvedSet.has(lastStep.approvalId) && !approvedSet.has(`${lastStep.approvalId}__changed`);
  }, [execution.columns, visibleCounts, approvedSet]);

  const getColumnWaiting = useCallback((colIdx) => {
    const col = execution.columns[colIdx];
    if (col.startPhase === 'immediate') return false;
    const dep = col.startPhase.replace('after:', '');
    return !milestones.has(dep);
  }, [execution.columns, milestones]);

  // Advance execution steps
  useEffect(() => {
    if (!started) return;
    timerRef.current = setInterval(() => {
      setVisibleCounts(prev => {
        const next = [...prev];
        let anyAdvanced = false;
        execution.columns.forEach((col, idx) => {
          if (next[idx] >= col.steps.length) return;
          if (getColumnBlocked(idx)) return;
          if (getColumnWaiting(idx)) {
            if (next[idx] === 0 && col.steps[0]?.type === 'waiting') {
              next[idx] = 1;
              anyAdvanced = true;
            }
            return;
          }
          const newCount = next[idx] + 1;
          if (newCount <= col.steps.length) {
            next[idx] = newCount;
            anyAdvanced = true;
            const revealedStep = col.steps[newCount - 1];
            if (revealedStep?.milestone) {
              setMilestones(ms => new Set([...ms, revealedStep.milestone]));
            }
          }
        });
        return anyAdvanced ? next : prev;
      });
    }, STEP_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [started, execution.columns, getColumnBlocked, getColumnWaiting]);

  // Report progress
  // Report progress — use ref to avoid re-render loop from unstable callback
  const onProgressRef = useRef(onProgressUpdate);
  onProgressRef.current = onProgressUpdate;

  useEffect(() => {
    const totalSteps = execution.columns.reduce((sum, col) => sum + col.steps.length, 0);
    const completedSteps = visibleCounts.reduce((sum, count, idx) => {
      const col = execution.columns[idx];
      return sum + col.steps.slice(0, count).filter(s => s.type === 'result' || (s.type === 'hitl' && approvedSet.has(s.approvalId))).length;
    }, 0);
    const hitlPending = execution.columns.some((_, idx) => getColumnBlocked(idx));
    onProgressRef.current?.({ totalSteps, completedSteps, hitlPending, title: execution.title });
  }, [visibleCounts, approvedSet, execution, getColumnBlocked]);

  // Auto-start
  useEffect(() => {
    const timer = setTimeout(() => setStarted(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const addColumnMessage = useCallback((agent, msg) => {
    setColumnChats(prev => ({
      ...prev,
      [agent]: [...(prev[agent] || []), msg],
    }));
  }, []);

  const handleApprove = (approvalId) => {
    setApprovedSet(prev => new Set([...prev, approvalId]));
    // If this is a revised HITL approval, also approve the original and trigger cascade
    if (approvalId.endsWith('__revised')) {
      const originalId = approvalId.replace('__revised', '');
      setApprovedSet(prev => new Set([...prev, approvalId, originalId]));
      // Find the revised-hitl message to check for cascade
      Object.values(columnChats).forEach(msgs => {
        msgs.forEach(msg => {
          if (msg.role === 'revised-hitl' && msg.approvalId === approvalId && msg.hasCascade && msg.cascadeEffects) {
            // Show cascade HITL card
            setTimeout(() => {
              setPendingCascade({ sourceAgent: msg.agent, effects: msg.cascadeEffects });
            }, 500);
          }
        });
      });
    }
  };

  const handleRequestChanges = (approvalId) => {
    const msg = changeMessages[approvalId];
    if (!msg) return;
    const col = execution.columns.find(c => c.steps.some(s => s.approvalId === approvalId));
    if (!col) return;
    setActiveAgent(col.agent);
    setChatInput('');
    setTypingTarget(msg);
    setTypingIndex(0);
    setPendingAction({ type: 'request-changes', approvalId, agent: col.agent });
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleShareDetails = (stepId) => {
    const config = shareDetails[stepId];
    if (!config) return;
    setActiveAgent(config.agent);
    setChatInput('');
    setTypingTarget(config.prompt);
    setTypingIndex(0);
    setPendingAction({ type: 'share-details', stepId, agent: config.agent });
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Send message — uses ref to always access latest state
  handleSendRef.current = () => {
    const text = (typingTarget || chatInput || '').trim();
    // Use pendingAction's agent if set, otherwise activeAgent
    const targetAgent = pendingAction?.agent || activeAgent;
    if (!text || !targetAgent) return;
    const action = pendingAction;

    setTypingTarget(null);
    setTypingIndex(0);
    setChatInput('');
    setPendingAction(null);

    addColumnMessage(targetAgent, { role: 'user', text });

    setTimeout(() => {
      if (action?.type === 'share-details') {
        const config = shareDetails[action.stepId];
        if (config) {
          addColumnMessage(targetAgent, {
            role: 'agent',
            agent: config.agent,
            text: config.response,
            followUp: config.followUp,
            hitlShortcut: config.hitlShortcut,
          });
        }
      } else if (action?.type === 'request-changes') {
        const config = requestChanges[action.approvalId];
        if (config) {
          // Step 1: Agent acknowledges and processes the change
          addColumnMessage(targetAgent, {
            role: 'agent',
            agent: targetAgent,
            text: config.agentResponse,
          });
          // Step 2: After a delay, show "revising" thinking, then the revised HITL
          setTimeout(() => {
            addColumnMessage(targetAgent, {
              role: 'system',
              text: 'Revising deliverable...',
            });
            setTimeout(() => {
              // Mark original HITL as "changed" (not approved, but no longer blocking)
              setApprovedSet(prev => new Set([...prev, `${action.approvalId}__changed`]));
              // Step 3: Show revised HITL with Approve/Request Changes
              const revisedApprovalId = `${action.approvalId}__revised`;
              addColumnMessage(targetAgent, {
                role: 'revised-hitl',
                agent: targetAgent,
                approvalId: revisedApprovalId,
                originalApprovalId: action.approvalId,
                text: config.revisedHitl || 'Revised deliverable ready for review.',
                hasCascade: config.cascade && config.cascadeEffects?.length > 0,
                cascadeEffects: config.cascadeEffects,
              });
            }, 1500);
          }, 1000);
        }
      } else {
        addColumnMessage(targetAgent, {
          role: 'agent',
          agent: targetAgent,
          text: `I'll take that into account and adjust my approach accordingly. Let me update my current workstream.`,
        });
      }
    }, 800);
  };

  const handleSendMessage = () => handleSendRef.current?.();

  const handleApproveCascade = () => {
    if (!pendingCascade) return;
    // Notify affected agents
    pendingCascade.effects.forEach(effect => {
      addColumnMessage(effect.agent, {
        role: 'cascade-notify',
        fromAgent: pendingCascade.sourceAgent,
        text: `${AGENT_NAMES[pendingCascade.sourceAgent]}: Change approved and cascading — ${effect.description}. Adjusting my workstream.`,
      });
    });
    // Animate cascade steps one-by-one with delay
    let stepDelay = 1200;
    pendingCascade.effects.forEach(effect => {
      (effect.steps || []).forEach(step => {
        setTimeout(() => {
          setCascadeStepsMap(prev => ({
            ...prev,
            [effect.agent]: [...(prev[effect.agent] || []), step],
          }));
        }, stepDelay);
        stepDelay += 1500;
      });
    });
    addColumnMessage(pendingCascade.sourceAgent, {
      role: 'system',
      text: `Changes propagating to ${pendingCascade.effects.map(e => AGENT_NAMES[e.agent]).join(' and ')}.`,
    });
    setPendingCascade(null);
  };

  const handleDeclineCascade = () => {
    if (!pendingCascade) return;
    addColumnMessage(pendingCascade.sourceAgent, {
      role: 'system',
      text: `Changes kept local. Other agents continue with original plan.`,
    });
    setPendingCascade(null);
  };

  const handleApproveHitl = (approvalId) => {
    handleApprove(approvalId);
    const col = execution.columns.find(c => c.steps.some(s => s.approvalId === approvalId));
    if (col) addColumnMessage(col.agent, { role: 'system', text: 'Approved. Execution continuing.' });
  };

  const handleRequestChangesHitl = (approvalId) => {
    handleRequestChanges(approvalId);
  };

  // Typing animation
  useEffect(() => {
    if (!typingTarget || typingIndex >= typingTarget.length) return;
    const timer = setTimeout(() => {
      setChatInput(typingTarget.slice(0, typingIndex + 1));
      setTypingIndex(prev => prev + 1);
    }, TYPING_SPEED);
    return () => clearTimeout(timer);
  }, [typingTarget, typingIndex]);

  // Auto-send after typing completes
  useEffect(() => {
    if (typingTarget && typingIndex >= typingTarget.length) {
      const timer = setTimeout(() => handleSendRef.current?.(), 600);
      return () => clearTimeout(timer);
    }
  }, [typingTarget, typingIndex]);

  const handleInput = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    setChatInput(e.target.value);
    setTypingTarget(null);
  };

  // Auto-resize during typing animation (when chatInput changes without onChange)
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 200) + 'px';
    }
  }, [chatInput]);

  return (
    <div className="flex flex-col h-full" data-testid="execution-view">
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 flex-shrink-0 border-b ${border}`}>
        <button onClick={onBack} className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-colors ${hoverBg}`} data-testid="exec-back-btn">
          <ArrowLeft className={`w-4 h-4 ${textSecondary}`} />
          <span className={`text-sm font-medium ${textPrimary}`}>{backText}</span>
        </button>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${isDark ? 'bg-red-400/10' : 'bg-red-600/10'}`}>
            <div className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            <span className={`text-[10px] font-medium ${isDark ? 'text-red-400' : 'text-red-600'}`}>Live Execution</span>
          </div>
          <span className={`text-xs ${textSecondary}`}>{execution.riskTitle}</span>
        </div>
      </div>

      {/* Execution columns */}
      <div className="flex-1 min-h-0 overflow-hidden px-3 py-3">
        <div className={`grid gap-3 h-full`} style={{ gridTemplateColumns: `repeat(${execution.columns.length}, 1fr)` }}>
          {execution.columns.map((col, idx) => (
            <AgentColumn
              key={col.agent}
              column={col}
              isDark={isDark}
              visibleStepCount={visibleCounts[idx]}
              approvedSet={approvedSet}
              onApprove={handleApprove}
              onRequestChanges={handleRequestChanges}
              onShareDetails={handleShareDetails}
              isBlocked={getColumnBlocked(idx)}
              cascadeSteps={cascadeStepsMap[col.agent] || []}
              chatMessages={columnChats[col.agent] || []}
              isActiveChat={activeAgent === col.agent}
              pendingCascade={pendingCascade?.sourceAgent === col.agent ? pendingCascade : null}
              onApproveCascade={handleApproveCascade}
              onDeclineCascade={handleDeclineCascade}
              onApproveHitl={handleApproveHitl}
              onRequestChangesHitl={handleRequestChangesHitl}
              shareDetailsMap={shareDetails}
            />
          ))}
        </div>
      </div>

      {/* Chat input — matching home / org pulse design */}
      <div className="flex justify-center pb-4 px-4 pt-2 flex-shrink-0">
        <div className="w-[80%]">
          {/* Input box */}
          <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
            <div className="px-4 pt-3 pb-2">
              <textarea
                ref={inputRef}
                value={chatInput}
                onChange={handleInput}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                placeholder={`Message ${AGENT_NAMES[activeAgent]}...`}
                rows={1}
                className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${textPrimary} placeholder:${textSecondary}`}
                style={{ height: 'auto', maxHeight: '200px', overflow: chatInput.split('\n').length > 8 ? 'auto' : 'hidden' }}
                data-testid="exec-chat-input"
              />
            </div>
            <div className="flex items-center justify-between px-3 pb-3">
              <div className="flex items-center gap-1">
                {/* All 4 agents inline, selected one highlighted */}
                {execution.columns.map((col) => {
                  const isSelected = activeAgent === col.agent;
                  return (
                    <button
                      key={col.agent}
                      onClick={() => setActiveAgent(col.agent)}
                      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${
                        isSelected
                          ? (isDark ? 'bg-white/[0.08] ring-1 ring-indigo-500/40' : 'bg-indigo-50 ring-1 ring-indigo-300/60')
                          : hoverBg
                      }`}
                      data-testid={`exec-agent-${col.agent}`}
                    >
                      <img src={AGENT_THUMBS[col.agent]} alt="" className="w-5 h-5 rounded-full object-cover" />
                      <span className={`text-xs font-medium ${isSelected ? textPrimary : textSecondary}`}>{AGENT_NAMES[col.agent]}</span>
                    </button>
                  );
                })}
                <button className={`p-1.5 rounded-lg transition-colors ${hoverBg}`} data-testid="exec-attach-btn">
                  <Paperclip className={`w-3.5 h-3.5 ${textSecondary}`} />
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <button className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${hoverBg}`} data-testid="exec-voice-btn">
                  <Mic className={`w-4 h-4 ${textSecondary}`} />
                </button>
                <button
                  onClick={handleSendMessage}
                  className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${
                    chatInput.trim()
                      ? 'bg-gradient-to-br from-indigo-500 to-purple-600'
                      : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')
                  }`}
                  data-testid="exec-chat-send"
                >
                  <Send className={`w-3.5 h-3.5 ${chatInput.trim() ? 'text-white' : textSecondary}`} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeSlideIn {
          animation: fadeSlideIn 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default V4ExecutionView;
